/**
 *  <search-fallback> — second-chance search.
 *
 *  When Shopify's search finds nothing for a term, this element asks an external
 *  service for products that do match:
 *
 *      GET <endpoint>/search?query=<term>&shop=<shop>
 *      200 -> { query, count, handles: [...] }
 *      404 -> the service has no match either (nothing is rendered)
 *
 *  The returned handles are turned into real theme markup by requesting the
 *  `search-fallback-results` section, so the products are the same `card-product`
 *  the rest of the theme renders — prices, badges, swatches and quick add included.
 *
 *  Two modes:
 *    drawer — listens for `search:no-results` from <predictive-search> and replaces
 *             the drawer's empty state with fallback products.
 *    grid   — runs on load on a /search page that returned nothing and fills the
 *             empty product grid.
 *
 *  Request budget (drawer mode gets an event per keystroke that finds nothing):
 *    - terms shorter than `data-min-length` never leave the browser
 *    - `data-debounce` ms of quiet time before a term is looked up at all
 *    - every answer is cached per term, misses included, so re-typing, backspacing
 *      or re-opening the drawer costs no request
 *
 *  While a term is on its way to the service a skeleton is shown, so the shopper is
 *  not left looking at a final-sounding "no results" during the round trip. It is
 *  inserted next to the theme's own empty state rather than replacing it: if the
 *  fallback comes back with nothing, removing the skeleton is all it takes to be
 *  back where we started.
 *
 *  Every failure path is silent by design: if the service is down, 404s, or the
 *  section request fails, the theme's normal "no results" state simply stays.
 *
 *  The service must send `Access-Control-Allow-Origin` for the storefront origin,
 *  otherwise the browser blocks the response and the fallback never renders (one
 *  console warning is logged per element when a lookup fails).
 */
if (!customElements.get('search-fallback')) {
  class SearchFallback extends HTMLElement {
    // Markers the section uses to recognise a fallback request and pick the markup
    // variant, see sections/search-fallback-results.liquid.
    static get MARKERS() {
      return { drawer: 'sfb-rows--', grid: 'sfb-grid--' };
    }

    // Plenty for one session of typing, and keeps the map from growing forever.
    static get CACHE_LIMIT() {
      return 50;
    }

    // Enough placeholders to read as "products are coming", few enough to not push
    // the real content out of view when it arrives.
    static get SKELETON_COUNT() {
      return { drawer: 3, grid: 4 };
    }

    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '').trim().replace(/\/+$/, '');
      this.mode = this.dataset.mode === 'grid' ? 'grid' : 'drawer';
      this.limit = parseInt(this.dataset.limit, 10) || 6;

      const debounce = parseInt(this.dataset.debounce, 10);
      this.debounceDelay = isNaN(debounce) ? 300 : debounce;
      // A two-letter fragment gives a semantic model nothing to work with, so short
      // terms are dropped before they cost a lookup.
      const minLength = parseInt(this.dataset.minLength, 10);
      this.minLength = isNaN(minLength) ? 4 : minLength;

      // term -> markup, or '' when neither the service nor the store had anything.
      // Cached misses matter most: they keep a dead term from being looked up again
      // every time the shopper edits around it.
      this.cache = new Map();
      this.requestToken = 0;
      this.hasWarned = false;
      this.loadingEl = null;
      this.loadingHost = null;
      this.hiddenEls = [];

      if (!this.endpoint) return;

      if (this.mode === 'grid') {
        const term = (this.dataset.query || '').trim();
        // One shot on page load, so there is nothing to debounce.
        if (term.length >= this.minLength) this.search(term);
        return;
      }

      this.onNoResults = (event) => {
        if (!this.ownsEvent(event)) return;
        const term = event.detail.query;
        if (term) this.queueSearch(term.trim());
      };

      this.onResults = (event) => {
        if (!this.ownsEvent(event)) return;
        // Shopify answered with real products: drop anything still in flight so a
        // late fallback cannot overwrite them.
        clearTimeout(this.debounceTimer);
        this.requestToken++;
        this.hideLoading();
      };

      document.addEventListener('search:no-results', this.onNoResults);
      document.addEventListener('search:results', this.onResults);
    }

    disconnectedCallback() {
      clearTimeout(this.debounceTimer);
      this.hideLoading();
      document.removeEventListener('search:no-results', this.onNoResults);
      document.removeEventListener('search:results', this.onResults);
    }

    // A page can hold more than one search (header and search page), each with its
    // own results container — only react to the one this element belongs to.
    ownsEvent(event) {
      return !!(event.detail && event.detail.resultsId === this.dataset.resultsId);
    }

    // Debounced, cache-first entry point.
    queueSearch(term) {
      clearTimeout(this.debounceTimer);
      if (!term || term.length < this.minLength) {
        this.hideLoading();
        return;
      }

      if (this.cache.has(term)) {
        // Invalidates anything still in flight, then renders straight away.
        this.requestToken++;
        this.hideLoading();
        const markup = this.cache.get(term);
        if (markup) this.render(markup, term);
        return;
      }

      // Shown before the debounce, not after: the empty state is already on screen
      // at this point, so waiting would read as a settled answer.
      this.showLoading();
      this.debounceTimer = setTimeout(() => this.search(term), this.debounceDelay);
    }

    search(term) {
      // Typing keeps firing searches; only the newest one may render.
      const token = ++this.requestToken;
      this.classList.add('loading');
      this.showLoading();

      this.fetchHandles(term)
        .then((handles) => {
          if (token !== this.requestToken) return null;
          return handles.length ? this.fetchProductsMarkup(handles) : '';
        })
        .then((markup) => {
          if (token !== this.requestToken || markup === null) return;
          this.remember(term, markup);
          if (markup) this.render(markup, term);
        })
        .catch((error) => {
          // Keep the theme's own empty state, but leave a trace: a blocked CORS
          // response or a sleeping service looks just like a miss.
          if (!this.hasWarned) {
            this.hasWarned = true;
            console.warn('[search-fallback] lookup failed:', error);
          }
        })
        .finally(() => {
          // A newer search owns the placeholder by now, so only the current one may
          // take it down.
          if (token !== this.requestToken) return;
          this.classList.remove('loading');
          this.hideLoading();
        });
    }

    // Placeholder cards for the wait. Kept out of the way of everything else: it is
    // a single node this element owns and removes again, so a miss leaves the
    // theme's own markup exactly as it found it.
    showLoading() {
      if (this.loadingEl && this.loadingEl.isConnected) return;

      const target = this.getTarget();
      if (!target) return;

      const host = this.getColumn(target) || target;
      const node = document.createElement('div');
      node.className = `search-fallback-loading search-fallback-loading--${this.mode}`;
      // Nothing here carries meaning, and the products that follow announce
      // themselves — so it stays out of the accessibility tree.
      node.setAttribute('aria-hidden', 'true');
      node.innerHTML = this.skeletonMarkup();

      host.appendChild(node);
      if (this.mode === 'drawer') {
        // The marker takes the "no results" copy down for the moment: the answer is
        // not in yet, so it should not be on screen.
        host.setAttribute('data-search-fallback-pending', '');
      }

      // The search page's empty state lives outside this element, so CSS cannot
      // reach it from the marker — it is hidden by hand and put back on a miss.
      this.hideEmptyState();

      this.loadingEl = node;
      this.loadingHost = host;
    }

    hideLoading() {
      if (this.loadingEl) this.loadingEl.remove();
      if (this.loadingHost) this.loadingHost.removeAttribute('data-search-fallback-pending');
      this.loadingEl = null;
      this.loadingHost = null;
      // Only ever restores what the placeholder hid: once products render, they take
      // the empty state off this list and it stays down for good.
      this.restoreEmptyState();
    }

    hideEmptyState() {
      const selector = this.dataset.hide;
      if (!selector) return;

      document.querySelectorAll(selector).forEach((el) => {
        if (el.hasAttribute('hidden')) return;
        el.setAttribute('hidden', '');
        el.style.display = 'none';
        this.hiddenEls.push(el);
      });
    }

    restoreEmptyState() {
      this.hiddenEls.forEach((el) => {
        el.removeAttribute('hidden');
        el.style.display = '';
      });
      this.hiddenEls = [];
    }

    skeletonMarkup() {
      const count = SearchFallback.SKELETON_COUNT[this.mode];
      const card =
        '<div class="search-fallback-loading__card">' +
        '<span class="search-fallback-loading__media"></span>' +
        '<span class="search-fallback-loading__line"></span>' +
        '<span class="search-fallback-loading__line search-fallback-loading__line--short"></span>' +
        '</div>';

      return (
        '<span class="search-fallback-loading__rule"></span>' +
        '<div class="search-fallback-loading__cards">' +
        card.repeat(Math.min(count, this.limit || count)) +
        '</div>'
      );
    }

    remember(term, markup) {
      if (this.cache.size >= SearchFallback.CACHE_LIMIT) {
        this.cache.delete(this.cache.keys().next().value);
      }
      this.cache.set(term, markup);
    }

    // Asks the fallback service for matching products. A 404 means it found nothing
    // either, which is an expected answer rather than an error.
    fetchHandles(term) {
      const shop = (window.Shopify && window.Shopify.shop) || '';
      const url = `${this.endpoint}/search?query=${encodeURIComponent(term)}&shop=${encodeURIComponent(shop)}`;

      return fetch(url)
        .then((response) => {
          if (response.status === 404) return null;
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then((data) => {
          if (!data || !Array.isArray(data.handles)) return [];
          return data.handles.filter((handle) => typeof handle === 'string' && handle.length).slice(0, this.limit);
        });
    }

    // Renders the handles through the theme so the fallback products look exactly
    // like normal results. Resolves to the markup, or '' when none of the handles
    // exist in this store.
    fetchProductsMarkup(handles) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // The section is rendered from the layout and has no idea which scheme the
      // drawer or the search page is running in — so it travels with the handles.
      const scheme = this.dataset.scheme || 'scheme-1';
      const query = `${SearchFallback.MARKERS[this.mode]}${scheme}--${handles.join(',')}`;
      const url = `${root}search?q=${encodeURIComponent(query)}&type=product&section_id=search-fallback-results`;

      return fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const selector = this.mode === 'grid' ? '[data-search-fallback-grid]' : '[data-search-fallback-rows]';
          const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector(selector);
          // The section renders nothing when every handle was unknown to this store.
          return fresh ? fresh.outerHTML : '';
        });
    }

    render(markup, term) {
      const target = this.getTarget();
      if (!target) return;

      const column = this.getColumn(target);
      if (column) {
        // Suggestions and articles stay put; only the empty product side is filled.
        const block = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-search-fallback-block]');
        if (!block) return;
        column.innerHTML = '';
        column.appendChild(block);
      } else {
        target.innerHTML = markup;
      }
      target.setAttribute('data-search-fallback-for', term);

      this.hideEmptyState();
      // There are products now, so the empty state is not coming back — dropping the
      // list keeps the placeholder from restoring it on its way out.
      this.hiddenEls = [];
    }

    // Resolved late: the drawer replaces its results markup on every keystroke.
    getTarget() {
      if (this.mode === 'grid') return this;

      const target = document.getElementById(this.dataset.resultsId || '');
      if (!target) return null;

      // The drawer may have rendered real products while the fallback was in flight —
      // those win, so leave the panel alone. Shopify's list carries an id the fallback
      // markup does not, so the fallback's own cards never read as a match.
      const products = target.querySelector('#predictive-search-results-products-list');
      return products && products.children.length ? null : target;
    }

    // Shopify prints its empty state only when nothing at all matched. When it came
    // back with spelling suggestions or articles instead, just the product column is
    // empty — filling that keeps the rest of its answer on screen.
    getColumn(target) {
      if (this.mode === 'grid') return null;
      return target.querySelector('.search__box-products');
    }
  }

  customElements.define('search-fallback', SearchFallback);
}
